import { Injectable, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AppStorage } from "@bbwt/utils/app-storage";
import { OrganizationService } from "@main/companies";
import { OrganizationSelfRegisterInfo } from "@main/companies/organization";
import { catchError, firstValueFrom, map, of, switchMap } from "rxjs";
import { removePathParam } from "@bbwt/utils/routes";
import { Location } from "@angular/common";

@Injectable()
export class SelfRegisterInfoService {
    private _clinic = "";
    private _goToUrl: string = null;

    private readonly route = inject(ActivatedRoute);
    private readonly location = inject(Location);
    private readonly organizationService = inject(OrganizationService);

    readonly clinic$ = this.route.queryParams.pipe(
        map(params => params.clinic ?? AppStorage.getItemFromSession<string>("clinic"))
    );

    readonly externalPatientId$ = this.route.queryParams.pipe(
        map(params => params.externalpatientid ?? AppStorage.getItemFromSession<string>("externalpatientid"))
    );

    readonly selfRegisterInfo$ = this.clinic$.pipe(
        switchMap(clinic => this.organizationService.isSelfRegistrationEnabled(clinic)),
        catchError(_ => of({ selfRegisterPatientURLEnabled: false, nhsEnabled: false } as OrganizationSelfRegisterInfo)),
        map(info => ({
            url: info.selfRegisterPatientURLEnabled,
            loginMode: info.selfRegisterPatientUrlLoginMode,
            nhs: info.nhsEnabled,
            orgName: info.name,
            logoUrl: info.logoImage?.url,
            externalIdEnabled: info.externalPatientIdSavingEnabled,
            welcomeMessageTitle: info.selfRegisterPatientWelcomeTitle,
            welcomeMessageSubtitle: info.selfRegisterPatientWelcomeSubtitle
        }))
    );

    readonly goToUrl$ = this.route.queryParams.pipe(
        map(params => params.goToUrl)
    );

    readonly returnUrl$ = this.route.queryParams.pipe(
        map(params => params.returnUrl)
    );

    constructor() {
        this.init();
    }

    get clinic() {
        return this._clinic;
    }

    get goToUrl() {
        return this._goToUrl ? this._goToUrl.split("?")[0]: null;
    }

    get fullGoToUrl(): string {
        return this._goToUrl;
    }

    async getReturnUrl(): Promise<string> {
        return (await firstValueFrom(this.returnUrl$)) ?? this._goToUrl;
    }

    public clearExternalId() {
        const path = removePathParam(this.location.path(), "externalpatientid");
        AppStorage.setItemForSession("externalpatientid", null);
        this.location.replaceState(path);
    }

    private async init() {
        this._clinic = await firstValueFrom(this.clinic$);
        this._goToUrl = (await firstValueFrom(this.goToUrl$)) ?? (await firstValueFrom(this.returnUrl$));
    }
}
